import { EventEmitter, Injectable } from '@angular/core';
//tslint:disable-next-line:no-implicit-dependencies
import { ModalOptions } from '@ionic/core';
import { ModalController } from '@ionic/angular';
import { firstValueFrom, from, interval, of, race, Subject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';

@Injectable({
  providedIn: 'root'
})
export class ModalControllerService {
  constructor(
    private readonly store: Store,
    private readonly modalController: ModalController
  ) {}

  public async show(
    opts: ModalOptions,
    callback?: (value: any) => any,
    keepFocus?: boolean,
    ignoreIos14Mode?: boolean,
    autocompleteResultEmitter?: EventEmitter<any>
  ): Promise<HTMLIonModalElement> {
    let existingModalBackDrop;
    const routerOutlet = document.getElementsByTagName('ion-router-outlet')[0];
    const existingModal = document.getElementsByTagName('ion-modal')[0];
    const selectFromValuePicker = new EventEmitter<any>();
    const autocompleteResult = new EventEmitter<any>();
    const cancel = new EventEmitter();
    const destroyed = new Subject<void>();

    if (existingModal && !existingModal.classList.contains('qvs-input-inline-modal')) {
      existingModalBackDrop = existingModal.shadowRoot.querySelector('ion-backdrop');
    }
    let presentingElement: any = routerOutlet && !ignoreIos14Mode ? routerOutlet : undefined;
    if (existingModalBackDrop) {
      presentingElement = existingModalBackDrop;
    }
    const modal = await this.modalController.create({
      ...opts,
      componentProps: {
        ...opts.componentProps,
        autoFocus: !!keepFocus,
        autocompleteResult,
        selectFromValuePicker,
        cancel
      },
      showBackdrop: opts.showBackdrop ?? true,
      canDismiss: opts.canDismiss ?? true,
      keyboardClose: opts.keyboardClose ?? false,
      animated: opts.animated ?? true,
      presentingElement
    });
    this.applyAutoFocus(modal);
    await modal.present();

    if (autocompleteResultEmitter) {
      autocompleteResult.pipe(takeUntil(destroyed)).subscribe((data) => autocompleteResultEmitter.emit(data));
    }

    firstValueFrom(
      race([selectFromValuePicker, cancel, from(modal.onWillDismiss()).pipe(switchMap((result) => of(result.data)))])
    ).then((value) => {
      if (callback) {
        callback(value);
      }
      modal.dismiss().then(() => {
        if (!keepFocus) {
          this.unsetFocus().then();
        }
        destroyed.next();
      });
    });

    return modal;
  }

  public async showInlineModal(
    targetElement: HTMLElement,
    opts: ModalOptions,
    callback?: (value: any) => any
  ): Promise<HTMLIonModalElement> {
    const selectFromValuePicker = new EventEmitter<any>();
    const cancel = new EventEmitter();
    const modal = await this.modalController.create({
      ...opts,
      componentProps: {
        ...opts.componentProps,
        selectFromValuePicker,
        cancel
      },
      showBackdrop: true,
      canDismiss: true,
      backdropDismiss: true,
      animated: false,
      keyboardClose: false,
      cssClass: 'qvs-input-inline-modal'
    });
    targetElement.parentNode.parentNode.insertBefore(modal, targetElement.parentNode.nextSibling);
    await modal.present();
    modal.setAttribute('tabindex', '1');

    firstValueFrom(
      race([selectFromValuePicker, cancel, from(modal.onWillDismiss()).pipe(switchMap((result) => of(result.data)))])
    ).then((value) => {
      modal.dismiss().then(() => {
        if (callback) {
          callback(value);
        }
      });
    });

    return modal;
  }

  private applyAutoFocus(modal: HTMLIonModalElement) {
    const inputElements = modal.getElementsByTagName('input');
    if (inputElements.length > 0) {
      inputElements.item(0).focus();
    } else {
      this.unsetFocus().then();
    }
  }

  private async unsetFocus() {
    interval(10)
      .pipe(take(50))
      .subscribe(() => {
        const focusElement = document.querySelector(':focus');
        if (focusElement) {
          (focusElement as any).blur();
        }
      });
  }
}
